import {
	AccessKeyManagement,
	ApplicationsPortal,
	AuditManagement,
	OutboundApplications,
	RoleManagement,
	TenantProfile,
	UserManagement,
	UserProfile,
	useDescope
} from '@descope/react-sdk';
import { useTheme } from '@mui/material';
import { useAdminPortalConfig } from 'api/adminPortalConfig';
import React, { useCallback, useRef } from 'react';
import { useThemeCtx } from 'state/theme/ThemeCtx';
import { useLoggedInUser, useTenant } from 'state/user';
import { TENANT_SCOPED_WIDGET_TYPES } from 'state/widget/widget';
import { ConfigWidget } from 'types/adminPortalConfig';
import { WidgetType } from 'types/widgets';

/**
 * Give every widget's shadow-DOM root container the same background as the
 * `adminPortalContentPanel` wrapper it sits in (`theme.portalStyles?.content`), so a
 * customized "Portal Main section" color reaches the widget too.
 *
 * `descope-container` resolves each style as
 * `var(--descope-container-x__override, var(--descope-container-x, <default>))`, so
 * setting `--descope-container-background-color__override` wins over the widget's own
 * default. We adopt one constructable stylesheet into the shadow root at
 * element-upgrade time (no MutationObserver, no flash) and keep it in sync as the
 * theme/mode changes; unset falls back to the panel's own default.
 */
const widgetSurfaceStyle = (background: string): string => `
	descope-container[id="ROOT"] {
		--descope-container-background-color__override: ${background};
	}
`;

const SHEET = '__adminPortalWidgetSurfaceSheet';

type WidgetHost = HTMLElement & { [SHEET]?: CSSStyleSheet };

/* istanbul ignore next */
const adoptWidgetSurface = (host: WidgetHost, css: string): void => {
	let sheet = host[SHEET];
	try {
		if (!sheet) {
			sheet = new CSSStyleSheet();
			host[SHEET] = sheet;
		}
		sheet.replaceSync(css);
	} catch {
		/* constructable stylesheets unsupported (e.g. jsdom, older browsers) */
		return;
	}

	const attach = (): void => {
		const shadow = host.shadowRoot;
		if (!shadow || shadow.adoptedStyleSheets.includes(sheet!)) {
			return;
		}
		shadow.adoptedStyleSheets = [...shadow.adoptedStyleSheets, sheet!];
	};

	if (host.shadowRoot) {
		attach();
	} else if (
		typeof customElements !== 'undefined' &&
		host.localName.includes('-')
	) {
		customElements.whenDefined(host.localName).then(attach);
	}
};

// Ref that styles the widget's root container to match the Main-section (`content`)
// background, updating the adopted sheet in place on theme/mode change.
const useAdminPortalWidgetSurfaceRef = () => {
	const theme = useTheme();
	const content = theme.portalStyles?.content;
	// when the Main section carries a background image, keep the widget surface
	// transparent so the image shows through rather than being covered by a fill
	const background = content?.backgroundImage
		? 'transparent'
		: (content?.backgroundColor ?? 'var(--descope-colors-surface-main)');

	return useCallback(
		(el: HTMLElement | null): void => {
			/* istanbul ignore next */
			if (el) {
				adoptWidgetSurface(el as WidgetHost, widgetSurfaceStyle(background));
			}
		},
		[background]
	);
};

interface IWidgetViewProps {
	widget: ConfigWidget;
}

type UserProfileProps = React.ComponentProps<typeof UserProfile>;

const ProfileWidget: React.FC<{
	widgetId: string;
	styleId: UserProfileProps['styleId'];
	theme: UserProfileProps['theme'];
	surfaceRef: (el: HTMLElement | null) => void;
}> = ({ widgetId, styleId, theme, surfaceRef }) => {
	const sdk = useDescope();
	const cleanup = useRef<(() => void) | undefined>(undefined);

	// The profile flow's `success` event is dispatched on the inner descope-wc
	// (non-bubbling, non-composed) so it never leaves the widget's shadow root.
	// Catch it in the capture phase on the shadow root and reload the user, so a
	// picture change propagates across the app (e.g. the avatar in the menu).
	const attachSuccessListener = useCallback(
		(el: HTMLElement | null) => {
			cleanup.current?.();
			cleanup.current = undefined;

			const shadow = el?.shadowRoot;
			if (!shadow) {
				return;
			}

			const reloadUser = () => {
				sdk.me();
			};
			shadow.addEventListener('success', reloadUser, true);
			cleanup.current = () =>
				shadow.removeEventListener('success', reloadUser, true);
		},
		[sdk]
	);

	const ref = useCallback(
		(el: HTMLElement | null) => {
			surfaceRef(el);
			attachSuccessListener(el);
		},
		[surfaceRef, attachSuccessListener]
	);

	return (
		<UserProfile
			ref={ref}
			widgetId={widgetId}
			styleId={styleId}
			theme={theme}
			logger={{}}
		/>
	);
};

const WidgetView: React.FC<IWidgetViewProps> = ({ widget }) => {
	const { tenant } = useTenant();
	const { user } = useLoggedInUser();
	const { data: appConfig } = useAdminPortalConfig();
	const { mode } = useThemeCtx();
	const widgetSurfaceRef = useAdminPortalWidgetSurfaceRef();

	/* istanbul ignore next  */
	if (!user || !appConfig) {
		throw new Error('View data is missing');
	}

	/* istanbul ignore next  */
	if (TENANT_SCOPED_WIDGET_TYPES.has(widget.type) && !tenant) {
		return null;
	}

	switch (widget.type) {
		case WidgetType.ROLE_MANAGEMENT: {
			return (
				<RoleManagement
					tenant={tenant!.tenantId}
					widgetId={widget.id}
					styleId={appConfig?.styleId}
					theme={mode}
					logger={{}}
					ref={widgetSurfaceRef}
				/>
			);
		}
		case WidgetType.AUDIT_MANAGEMENT: {
			return (
				<AuditManagement
					tenant={tenant!.tenantId}
					widgetId={widget.id}
					styleId={appConfig?.styleId}
					theme={mode}
					logger={{}}
					ref={widgetSurfaceRef}
				/>
			);
		}
		case WidgetType.ACCESS_KEY_MANAGEMENT: {
			return (
				<AccessKeyManagement
					tenant={tenant!.tenantId}
					widgetId={widget.id}
					styleId={appConfig?.styleId}
					theme={mode}
					logger={{}}
					ref={widgetSurfaceRef}
				/>
			);
		}
		case WidgetType.TENANT_PROFILE: {
			return (
				<TenantProfile
					tenant={tenant!.tenantId}
					widgetId={widget.id}
					styleId={appConfig?.styleId}
					theme={mode}
					logger={{}}
					ref={widgetSurfaceRef}
				/>
			);
		}
		case WidgetType.USER_MANAGEMENT: {
			return (
				<UserManagement
					tenant={tenant!.tenantId}
					widgetId={widget.id}
					styleId={appConfig?.styleId}
					theme={mode}
					logger={{}}
					ref={widgetSurfaceRef}
				/>
			);
		}
		case WidgetType.OUTBOUND_APPLICATIONS: {
			return (
				<OutboundApplications
					tenant={tenant!.tenantId}
					widgetId={widget.id}
					styleId={appConfig?.styleId}
					theme={mode}
					logger={{}}
					ref={widgetSurfaceRef}
				/>
			);
		}
		case WidgetType.APPLICATIONS_PORTAL: {
			// on logout refresh
			return (
				<ApplicationsPortal
					widgetId={widget.id}
					styleId={appConfig?.styleId}
					theme={mode}
					logger={{}}
					ref={widgetSurfaceRef}
				/>
			);
		}
		case WidgetType.USER_PROFILE: {
			return (
				<ProfileWidget
					widgetId={widget.id}
					styleId={appConfig?.styleId}
					theme={mode}
					surfaceRef={widgetSurfaceRef}
				/>
			);
		}

		/* istanbul ignore next  */
		default: {
			return null;
		}
	}
};

export default WidgetView;
